/**
 * Where a previewed file lives as a full page. The preview dialog mounts the
 * P&ID editor in-tree and everything else in an embedded iframe, and the
 * designer's File Preview note asks for "Open in New Tab" on top of that —
 * the same route, as a page of its own. One function builds it so the two
 * cannot drift.
 */
export type PreviewKind = 'graph' | 'document' | 'datasheet' | 'timeseries'

export interface PreviewOverlay {
  kind: PreviewKind
  /** For kind === 'graph' the GRAPH id (resolved from the document); otherwise the document id. */
  id: string
  /** Present for graph previews (multi-page P&ID support). */
  documentId?: string
  /** "1:uuid,2:uuid" for multi-page P&IDs. */
  pnidPages?: string
  title: string
}

export const externalPreviewRoute = (overlay: PreviewOverlay): string => {
  switch (overlay.kind) {
    case 'graph': {
      const params = new URLSearchParams({ editor: 'd3' })
      if (overlay.documentId) params.set('documentId', overlay.documentId)
      if (overlay.pnidPages) params.set('pnidPages', overlay.pnidPages)
      return `/graph-editor/${overlay.id}?${params.toString()}`
    }
    case 'datasheet':
      return `/datasheet/${overlay.id}`
    case 'timeseries':
      return `/timeseries/${overlay.id}`
    case 'document':
      return `/document/${overlay.id}`
  }
}

export const DATA_CATALOG_ROUTE = '/data-catalog'

/**
 * "Open in Data Catalog" (the designer's File Preview note: the dialog only
 * previews, anything more happens in the catalog). The catalog keeps its
 * search box in sessionStorage through `useSessionState`, so priming that key
 * with the file's name lands the reader on the catalog already narrowed to
 * it. A storage failure just means an unfiltered catalog.
 */
export const primeDataCatalogSearch = (term: string): void => {
  try {
    sessionStorage.setItem(DATA_CATALOG_SEARCH_KEY, JSON.stringify(term))
  } catch {
    // Private mode / sandboxed iframe: the navigation still happens.
  }
}
/** `DATA_CATALOG_STORAGE_KEYS.search` in `@/hooks/useSessionState`; repeated here so this module stays dependency-free. */
export const DATA_CATALOG_SEARCH_KEY = 'data-catalog:search'

/** The iframe route for non-graph previews; the graph kind mounts in-tree and has none. */
export const embeddedPreviewRoute = (overlay: PreviewOverlay): string =>
  overlay.kind === 'graph' ? '' : `${externalPreviewRoute(overlay)}?embed=1`
